import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { api, uploadUrl } from '../hooks/useApi';
import { LIBELLES_OPERATEURS } from '../utils/tracabilite';
import CourbeCuissonBatch from '../components/CourbeCuissonBatch';
import Icon from '../components/Icon';
import './FicheTracabilite.css';

const LABEL_CAT_FICHE = {
  viande:          'Viandes',
  produit_laitier: 'Produits laitiers',
  frais_legume:    'Légumes frais',
  frais_autre:     'Autres frais',
};

const ICON_CAT_FICHE = {
  viande:          'viande',
  produit_laitier: 'laitier',
  frais_legume:    'legume',
  frais_autre:     'package',
};

function fmtNombre(value) {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('fr-CA') : value;
}

function SectionHeader({ iconName, title }) {
  return (
    <div className="fiche-section-header">
      <span className="fiche-section-icon">
        <Icon name={iconName} size={16} />
      </span>
      <span className="fiche-section-title">{title}</span>
    </div>
  );
}

function InfoGrid({ items }) {
  return (
    <div className="fiche-grid">
      {items.map(({ label, value }, i) => (
        <div key={i} className="fiche-grid-item">
          <span>{label}</span>
          <strong>{value || '—'}</strong>
        </div>
      ))}
    </div>
  );
}

function FicheSkeleton() {
  return (
    <div className="fiche-skeleton">
      <div className="skeleton skel-fiche-title" />
      <div className="skeleton skel-fiche-sub" />
      <div className="skeleton skel-fiche-card" />
    </div>
  );
}

export default function FicheTracabilite() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ficheRef = useRef(null);
  const [data, setData] = useState(null);
  const [liaisons, setLiaisons] = useState([]);
  const [erreur, setErreur] = useState('');
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/batches/${id}/tracabilite`),
      api.get(`/batches/${id}/transactions-liees`).catch(() => []),
    ])
      .then(([fiche, trans]) => {
        setData(fiche);
        setLiaisons(trans || []);
      })
      .catch((err) => setErreur(err.message || 'Erreur chargement fiche'))
      .finally(() => setLoading(false));
  }, [id]);

  const imprimer = () => window.print();

  const telechargerPdf = async () => {
    if (!ficheRef.current) return;
    setPdfLoading(true);
    try {
      await html2pdf().set({
        margin: 8,
        filename: `fiche-tracabilite-batch-${id}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      }).from(ficheRef.current).save();
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) return <FicheSkeleton />;

  if (erreur) return (
    <div className="fiche-page">
      <div className="error-inline">
        <Icon name="erreur" size={16} />
        {erreur}
      </div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="fiche-page rise-in">

      {/* ── Barre d'actions (écran seulement) ────────────────────────────── */}
      <div className="no-print">
        <div className="page-header" style={{ marginBottom: 0 }}>
          <div className="page-header-text">
            <h1 className="fiche-page-title">Fiche de traçabilité</h1>
            <p className="fiche-page-subtitle">Batch {data.batch_nom}</p>
          </div>
          <div className="fiche-actions">
            <button className="btn btn-ghost" onClick={() => navigate(-1)}>
              <Icon name="retour" size={15} />
              Retour
            </button>
            <button className="btn btn-ghost" onClick={imprimer}>
              <Icon name="document" size={15} />
              Imprimer
            </button>
            <button className="btn btn-primary" onClick={telechargerPdf} disabled={pdfLoading}>
              <Icon name="telecharger" size={15} />
              {pdfLoading ? 'Génération…' : 'Télécharger PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Fiche imprimable ─────────────────────────────────────────────── */}
      <div className="fiche-tracabilite" ref={ficheRef}>

        {/* En-tête */}
        <div className="fiche-entete">
          <div className="fiche-logo">ESCAMEL</div>
          <div className="fiche-entete-sous">Artisans du Fumoir S.E.N.C.</div>
          <h1>Fiche de traçabilité — Batch {data.batch_nom}</h1>
        </div>

        {/* ── Identification ─────────────────────────────────────────────── */}
        <div className="fiche-section">
          <SectionHeader iconName="liste" title="Identification" />
          <InfoGrid items={[
            { label: 'Batch ID',    value: data.batch_id },
            { label: 'Produit',     value: data.produit_label || data.produit },
            { label: 'Date prod.',  value: data.date_prod },
            { label: 'Opérateurs', value: LIBELLES_OPERATEURS[data.operateurs] || data.operateurs },
            { label: 'Heure début', value: data.heure_debut },
            { label: 'Heure fin',   value: data.heure_fin },
            { label: 'Durée totale', value: data.duree_production },
          ]} />
        </div>

        {/* ── Matière première ───────────────────────────────────────────── */}
        <div className="fiche-section">
          <SectionHeader iconName="viande" title="Matière première" />

          {/* Cas 0 : aucune liaison */}
          {(!data.ingredients_traces || data.ingredients_traces.length === 0) && (
            <>
              <div className="empty-state" style={{ padding: 'var(--space-4)' }}>
                <Icon name="liste" size={28} />
                <span>Aucune liaison de traçabilité enregistrée pour ce batch.</span>
              </div>
              <InfoGrid items={[
                { label: 'Poids cru',  value: data.poids_cru_kg ? `${fmtNombre(data.poids_cru_kg)} kg` : null },
                { label: 'Prix / kg',  value: data.prix_kg ? `${fmtNombre(data.prix_kg)} $` : null },
              ]} />
            </>
          )}

          {/* Cas 1 : un seul ingrédient tracé */}
          {data.ingredients_traces && data.ingredients_traces.length === 1 && (() => {
            const t = data.ingredients_traces[0];
            return (
              <InfoGrid items={[
                { label: 'Ingrédient',    value: t.matiere_nom },
                { label: 'Fournisseur',   value: t.fournisseur },
                { label: 'N° lot',        value: t.lot },
                { label: 'Date achat',    value: t.date_achat },
                { label: 'Date exp. MP',  value: t.date_expiration },
                { label: 'Poids utilisé', value: t.poids_utilise_kg ? `${fmtNombre(t.poids_utilise_kg)} kg` : null },
                { label: 'Poids cru total', value: data.poids_cru_kg ? `${fmtNombre(data.poids_cru_kg)} kg` : null },
                { label: 'Prix / kg',     value: data.prix_kg ? `${fmtNombre(data.prix_kg)} $` : null },
              ]} />
            );
          })()}

          {/* Cas 2 : plusieurs ingrédients */}
          {data.ingredients_traces && data.ingredients_traces.length > 1 && (
            <>
              <table className="esc-table">
                <thead>
                  <tr>
                    <th>Ingrédient</th>
                    <th>Fournisseur</th>
                    <th>N° lot</th>
                    <th>Date achat</th>
                    <th>Date exp.</th>
                    <th>Qté utilisée</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ingredients_traces.map((t, i) => (
                    <tr key={i}>
                      <td>{t.matiere_nom || '—'}</td>
                      <td>{t.fournisseur || '—'}</td>
                      <td>{t.lot || '—'}</td>
                      <td>{t.date_achat || '—'}</td>
                      <td>{t.date_expiration || '—'}</td>
                      <td>{t.poids_utilise_kg ? `${fmtNombre(t.poids_utilise_kg)} kg` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <InfoGrid items={[
                { label: 'Poids cru total', value: data.poids_cru_kg ? `${fmtNombre(data.poids_cru_kg)} kg` : null },
                { label: 'Prix / kg',       value: data.prix_kg ? `${fmtNombre(data.prix_kg)} $` : null },
              ]} />
            </>
          )}
        </div>

        {/* ── Production ─────────────────────────────────────────────────── */}
        <div className="fiche-section">
          <SectionHeader iconName="fumage" title="Production" />
          <InfoGrid items={[
            { label: 'Temp. interne atteinte', value: data.temp_interne_f ? `${fmtNombre(data.temp_interne_f)} °F` : null },
            { label: 'Durée cuisson / fumage',  value: data.heures_cuisson ? `${fmtNombre(data.heures_cuisson)} h` : null },
            { label: 'Yield global',            value: data.yield_global ? `${fmtNombre(data.yield_global)} %` : null },
            { label: 'Unités produites',        value: fmtNombre(data.nb_unites_produites) },
          ]} />
          <div className={`fiche-note${!data.notes_qualite ? ' fiche-note--neutre' : ''}`}>
            {data.notes_qualite || 'Aucune note de qualité'}
          </div>
        </div>

        {/* ── Produit fini ────────────────────────────────────────────────── */}
        <div className="fiche-section">
          <SectionHeader iconName="congelation" title="Produit fini" />
          <InfoGrid items={[
            { label: 'Date congélation',  value: data.date_congelation },
            { label: 'Temp. conservation', value: data.temp_conservation },
            { label: 'Date expiration',   value: data.date_expiration_produit },
            { label: 'SKU(s)',            value: data.sku_list?.join(', ') },
          ]} />
        </div>

        {/* ── Recette ─────────────────────────────────────────────────────── */}
        <div className="fiche-section">
          <SectionHeader iconName="liste" title="Recette utilisée" />
          {(!data.recette || data.recette.length === 0) ? (
            <div className="empty-state" style={{ padding: 'var(--space-4)' }}>
              <Icon name="liste" size={28} />
              <span>Aucune recette associée à ce batch.</span>
            </div>
          ) : (
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Ingrédient</th>
                  <th>Quantité</th>
                </tr>
              </thead>
              <tbody>
                {(data.recette || []).map((item) => (
                  <tr key={`${item.ingredient}-${item.unite}`}>
                    <td>{item.ingredient}</td>
                    <td>{fmtNombre(item.quantite)} {item.unite}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Coût & WooCommerce ──────────────────────────────────────────── */}
        <div className="fiche-section">
          <SectionHeader iconName="argent" title="Coût total" />
          <InfoGrid items={[
            { label: 'Coût matières', value: data.cout_matieres ? `${fmtNombre(data.cout_matieres)} $` : null },
            { label: 'Coût total',    value: data.cout_total ? `${fmtNombre(data.cout_total)} $` : null },
          ]} />
        </div>

        <div className="fiche-section">
          <SectionHeader iconName="boutique" title="WooCommerce" />
          {(!data.produits_wc || data.produits_wc.length === 0) ? (
            <div className="empty-state" style={{ padding: 'var(--space-4)' }}>
              <Icon name="boutique" size={28} />
              <span>Aucun produit WooCommerce associé.</span>
            </div>
          ) : (
            <table className="esc-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>WC ID</th>
                  <th>Stock</th>
                </tr>
              </thead>
              <tbody>
                {(data.produits_wc || []).map((item) => (
                  <tr key={item.sku}>
                    <td>{item.sku}</td>
                    <td>{item.wc_id || '—'}</td>
                    <td>{fmtNombre(item.stock)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Factures liées ──────────────────────────────────────────────── */}
        {liaisons.length > 0 && (
          <div className="fiche-section">
            <SectionHeader iconName="facture" title="Factures d'achat liées" />
            {['viande', 'produit_laitier', 'frais_legume', 'frais_autre'].map(cat => {
              const items = liaisons.filter(l => l.categorie_tracabilite === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat}>
                  <div className="fiche-cat-titre" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name={ICON_CAT_FICHE[cat] || 'package'} size={13} />
                    {LABEL_CAT_FICHE[cat]}
                  </div>
                  {items.map(l => (
                    <div key={l.id} className="fiche-liaison-row">
                      <InfoGrid items={[
                        { label: 'Ingrédient',   value: l.matiere_nom },
                        { label: 'Date achat',   value: l.date_trans?.slice(0, 10) },
                        { label: 'Fournisseur',  value: l.fournisseur },
                        { label: 'Montant',      value: `${Math.abs(l.montant || 0).toFixed(2)} $` },
                        ...(l.poids_utilise_kg ? [{ label: 'Qté utilisée', value: `${l.poids_utilise_kg} ${l.matiere_unite}` }] : []),
                        ...(l.description ? [{ label: 'Description', value: l.description }] : []),
                      ]} />
                      {l.recu_fichier && l.recu_type !== 'pdf' && (
                        <img
                          src={uploadUrl(`/uploads/recus/${l.recu_fichier}`)}
                          alt="Facture"
                          className="fiche-recu-image"
                        />
                      )}
                      {l.recu_fichier && l.recu_type === 'pdf' && (
                        <div className="fiche-recu-pdf">
                          <Icon name="document" size={13} />
                          Facture PDF : {l.recu_fichier}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Courbe de cuisson (écran uniquement) ────────────────────────── */}
        <div className="fiche-section no-print">
          <SectionHeader iconName="courbe" title="Courbe de cuisson" />
          <CourbeCuissonBatch batchId={Number(id)} produit={data.produit} sessionActive={false} />
        </div>

        {/* ── Incident / déviation ────────────────────────────────────────── */}
        <div className="fiche-section">
          <SectionHeader iconName="alerte" title="Incident / déviation" />
          <div className={`fiche-note${!data.incident ? ' fiche-note--neutre' : ''}`}>
            {data.incident || 'Aucun incident noté'}
          </div>
        </div>

        {/* ── Signature ───────────────────────────────────────────────────── */}
        <div className="fiche-signature">
          {['Validé par', 'Date', 'Signature'].map((label) => (
            <div key={label} className="fiche-signature-champ">
              <span className="fiche-signature-label">{label}</span>
              <div className="fiche-signature-ligne" />
            </div>
          ))}
        </div>

        <div className="fiche-pied">NEQ : 3381552556 — Stoneham-et-Tewkesbury, Québec G3C 0Z6</div>
      </div>
    </div>
  );
}
